import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Quote } from "lucide-react";
import { FadeUp, MaskLineInView } from "./motion";
import { CRITIC, NYT } from "../data/press";

function Attribution({ source, meta, testid }) {
  return (
    <div
      data-testid={testid}
      className="mt-10 flex flex-col gap-2 border-t border-line pt-6 sm:flex-row sm:items-center sm:justify-between"
    >
      <footer className="flex items-center gap-4">
        <span className="h-px w-12 bg-vermilion" />
        <cite className="font-mono text-[11px] not-italic uppercase tracking-[0.3em] text-bone">
          {source}
        </cite>
      </footer>
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">{meta}</span>
    </div>
  );
}

function CriticReview() {
  const [open, setOpen] = useState(false);
  return (
    <blockquote data-testid="critic-review" className="mt-24 border-t border-line pt-16">
      <p className="mb-6 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
        Review 02 — The Renaissance Academy
      </p>
      <p className="font-serif text-2xl font-light leading-snug tracking-tight text-bone sm:text-3xl lg:text-[2.4rem] lg:leading-[1.2]">
        <MaskLineInView>“{CRITIC.pull}”</MaskLineInView>
      </p>
      <FadeUp delay={0.15} className="mt-8 max-w-3xl text-base leading-relaxed text-ash sm:text-lg">
        {CRITIC.intro[1]}
      </FadeUp>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="full"
            data-testid="critic-review-full"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="max-w-3xl space-y-8 pt-10">
              <p className="text-base leading-relaxed text-ash sm:text-lg">{CRITIC.intro[0]}</p>
              {CRITIC.sections.map((s, i) => (
                <div key={s.heading} className="border-l border-vermilion/40 pl-6">
                  <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-vermilion">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mb-3 font-serif text-2xl text-bone">{s.heading}</h3>
                  <p className="text-base leading-relaxed text-ash sm:text-lg">{s.text}</p>
                </div>
              ))}
              <p className="text-base leading-relaxed text-ash sm:text-lg">{CRITIC.closing}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        data-testid="critic-review-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="group mt-8 inline-flex items-center gap-3 border border-line px-6 py-3 font-mono text-[11px] uppercase tracking-[0.3em] text-ash transition-colors duration-300 hover:border-bone hover:text-bone"
      >
        {open ? "Collapse review" : "Read the full review"}
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          strokeWidth={1.5}
        />
      </button>
      <Attribution source={CRITIC.source} meta={CRITIC.meta} testid="critic-attribution" />
    </blockquote>
  );
}

export default function Press() {
  return (
    <section
      id="press"
      data-testid="press-section"
      className="relative overflow-hidden border-t border-line py-20 sm:py-28"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 top-1/3 h-[30rem] w-[30rem] rounded-full bg-vermilion/10 blur-[140px]"
      />
      <div className="relative mx-auto grid max-w-[1600px] gap-12 px-6 sm:px-12 lg:grid-cols-12 lg:px-24">
        <FadeUp className="lg:col-span-3">
          <div className="lg:sticky lg:top-32">
            <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-vermilion">
              04 — In the Press
            </p>
            <Quote className="h-10 w-10 text-vermilion" strokeWidth={1} />
            <p className="mt-8 font-serif text-2xl italic text-smoke">
              On the record<span className="text-vermilion">.</span>
            </p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
              Two reviews
            </p>
          </div>
        </FadeUp>

        <div className="lg:col-span-9">
          <blockquote data-testid="press-quote">
            <p className="mb-6 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
              Review 01 — The Postmodern Polymath
            </p>
            <p className="font-serif text-2xl font-light leading-snug tracking-tight text-bone sm:text-3xl lg:text-[2.6rem] lg:leading-[1.2]">
              <MaskLineInView>“{NYT.lead}</MaskLineInView>
            </p>
            {NYT.body.map((t, i) => (
              <FadeUp
                key={i}
                delay={0.15 + i * 0.1}
                className="mt-6 max-w-3xl text-base leading-relaxed text-ash sm:text-lg"
              >
                {t}
                {i === NYT.body.length - 1 && "”"}
              </FadeUp>
            ))}
            <Attribution source={NYT.source} meta={NYT.meta} testid="press-attribution" />
          </blockquote>

          <CriticReview />
        </div>
      </div>
    </section>
  );
}
